const MAX_LONG_EDGE = 1920;
const SKIP_BITRATE = 8_000_000;
const TARGET_1080P_BITRATE = 6_000_000;
const MIN_BITRATE = 1_500_000;
const MAX_BITRATE = 8_000_000;
const TINY_FILE_BYTES = 25 * 1024 * 1024;
const OPFS_THRESHOLD_BYTES = 80 * 1024 * 1024;
const MIN_SAVINGS = 0.1;
const ABORTED_ERROR = 'Upload was aborted';

const compressionCache = new WeakMap();
const alreadyCompressed = new WeakSet();

function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw new Error(ABORTED_ERROR);
  }
}

function mp4FileName(name) {
  const raw = String(name || 'match-video');
  const base = raw.replace(/\.[^.]+$/, '') || 'match-video';
  return `${base}.mp4`;
}

function even(value) {
  const n = Math.max(2, Math.round(Number(value) || 0));
  return n % 2 === 0 ? n : n - 1;
}

function outputSizeFor(width, height) {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  if (Math.max(w, h) <= MAX_LONG_EDGE) {
    return { width: even(w), height: even(h), downscale: false };
  }
  const scale = MAX_LONG_EDGE / Math.max(w, h);
  return {
    width: even(w * scale),
    height: even(h * scale),
    downscale: true,
  };
}

function targetBitrate(width, height) {
  const pixels = Math.max(1, width * height);
  const scaled = TARGET_1080P_BITRATE * (pixels / (1920 * 1080));
  return Math.round(Math.min(MAX_BITRATE, Math.max(MIN_BITRATE, scaled)));
}

function skipped(file, reason) {
  return {
    file,
    compressed: false,
    skipped: true,
    reason,
    cleanup: null,
  };
}

async function loadMediabunny() {
  return import('mediabunny');
}

function canUseWebCodecs() {
  return typeof VideoEncoder !== 'undefined' && typeof VideoDecoder !== 'undefined';
}

async function createOutputTarget(mb, file, outputName) {
  const useOpfs = file.size >= OPFS_THRESHOLD_BYTES && typeof navigator?.storage?.getDirectory === 'function';
  if (!useOpfs) {
    const target = new mb.BufferTarget();
    return {
      target,
      fastStart: 'in-memory',
      async toFile() {
        const buffer = target.buffer;
        if (!buffer) return null;
        return new File([buffer], outputName, { type: 'video/mp4', lastModified: Date.now() });
      },
      cleanup: null,
    };
  }

  const root = await navigator.storage.getDirectory();
  const opfsName = `statbox-compress-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.mp4`;
  const handle = await root.getFileHandle(opfsName, { create: true });
  const writable = await handle.createWritable({ keepExistingData: false });
  return {
    target: new mb.StreamTarget(writable, { chunked: true, chunkSize: 4 * 1024 * 1024 }),
    fastStart: 'reserve',
    async toFile() {
      const fromDisk = await handle.getFile();
      return new File([fromDisk], outputName, { type: 'video/mp4', lastModified: Date.now() });
    },
    cleanup: async () => {
      try {
        await root.removeEntry(opfsName);
      } catch {
        /* already removed */
      }
    },
  };
}

async function transcode(file, { onProgress, signal }) {
  const mb = await loadMediabunny();
  throwIfAborted(signal);

  const canEncode = await mb.canEncodeVideo('avc');
  if (!canEncode) return skipped(file, 'encoder-unavailable');

  const input = new mb.Input({
    formats: mb.ALL_FORMATS,
    source: new mb.BlobSource(file),
  });

  let outputHandle = null;
  let conversion = null;
  let keepOutput = false;
  const onAbort = () => {
    conversion?.cancel?.().catch(() => {});
  };

  try {
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) return skipped(file, 'no-video-track');

    const [width, height, codec, canDecode] = await Promise.all([
      videoTrack.getDisplayWidth(),
      videoTrack.getDisplayHeight(),
      videoTrack.getCodec(),
      videoTrack.canDecode(),
    ]);
    if (!canDecode) return skipped(file, 'cannot-decode');

    let duration = await videoTrack.getDurationFromMetadata();
    if (!(duration > 1)) {
      duration = await input.computeDuration().catch(() => 0);
    }

    const bitrate = duration > 1 ? (file.size * 8) / duration : null;
    const size = outputSizeFor(width, height);
    const needsDownscale = size.downscale;
    const needsTranscode = Boolean(codec && codec !== 'avc');
    const needsBitrateCut = bitrate != null && bitrate > SKIP_BITRATE;
    const tiny = file.size < TINY_FILE_BYTES;

    if (tiny && !needsDownscale) return skipped(file, 'already-small');
    if (!needsDownscale && !needsTranscode && !needsBitrateCut) {
      return skipped(file, 'already-compressed');
    }

    throwIfAborted(signal);
    onProgress?.(0);
    const outputName = mp4FileName(file.name);
    outputHandle = await createOutputTarget(mb, file, outputName);
    const output = new mb.Output({
      format: new mb.Mp4OutputFormat({ fastStart: outputHandle.fastStart }),
      target: outputHandle.target,
    });

    const bitrateTarget = targetBitrate(size.width, size.height);
    const video = {
      codec: 'avc',
      quality: new mb.Quality({ bitrate: bitrateTarget, bitrateMode: 'variable' }),
      hardwareAcceleration: 'no-preference',
      keyFrameInterval: 4,
    };
    if (needsDownscale) {
      if (width >= height) video.width = size.width;
      else video.height = size.height;
    }

    conversion = await mb.Conversion.init({
      input,
      output,
      tracks: 'primary',
      video,
    });

    if (!conversion.isValid || !conversion.utilizedTracks.some((track) => track.isVideoTrack())) {
      await conversion.cancel().catch(() => {});
      return skipped(file, 'conversion-invalid');
    }

    if (signal) {
      if (signal.aborted) {
        await conversion.cancel().catch(() => {});
        throw new Error(ABORTED_ERROR);
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }

    conversion.onProgress = (progress) => {
      onProgress?.(Math.max(0, Math.min(100, Math.round((progress || 0) * 100))));
    };

    await conversion.execute();
    throwIfAborted(signal);
    onProgress?.(100);

    const compressedFile = await outputHandle.toFile();
    if (!compressedFile || compressedFile.size < 1024) {
      return skipped(file, 'empty-output');
    }
    if (compressedFile.size >= file.size * (1 - MIN_SAVINGS)) {
      await outputHandle.cleanup?.();
      return skipped(file, 'no-savings');
    }

    alreadyCompressed.add(compressedFile);
    keepOutput = true;
    return {
      file: compressedFile,
      compressed: true,
      skipped: false,
      originalSize: file.size,
      outputSize: compressedFile.size,
      cleanup: outputHandle.cleanup,
    };
  } finally {
    if (signal) signal.removeEventListener('abort', onAbort);
    if (!keepOutput) {
      await outputHandle?.cleanup?.().catch(() => {});
    }
    try {
      input.dispose();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Shrink large match footage in the browser before S3 upload.
 * Falls back to the original file when compression is unnecessary or unsupported.
 */
export async function compressVideoForUpload(file, { onProgress, signal } = {}) {
  if (!file) return skipped(file, 'missing-file');
  if (alreadyCompressed.has(file)) return skipped(file, 'already-output');

  const cached = compressionCache.get(file);
  if (cached) {
    onProgress?.(100);
    return cached;
  }

  throwIfAborted(signal);
  if (file.size < TINY_FILE_BYTES) return skipped(file, 'already-small');
  if (!canUseWebCodecs()) return skipped(file, 'webcodecs-unavailable');

  try {
    const result = await transcode(file, { onProgress, signal });
    if (result.compressed) {
      compressionCache.set(file, result);
    }
    return result;
  } catch (err) {
    if (signal?.aborted || String(err?.message || '') === ABORTED_ERROR) {
      throw new Error(ABORTED_ERROR);
    }
    console.warn('Video compression skipped:', err?.message || err);
    return skipped(file, 'compress-failed');
  }
}

export async function cleanupCompressedVideo(result, originalFile = null) {
  if (originalFile) {
    compressionCache.delete(originalFile);
  }
  if (!result?.cleanup) return;
  try {
    await result.cleanup();
  } catch {
    /* ignore */
  }
  if (result.file) {
    try {
      alreadyCompressed.delete(result.file);
    } catch {
      /* ignore */
    }
  }
}
